import type { JSX } from 'solid-js';
import { t } from '../i18n';
import type { DonateStep, StepState } from '../lib/donateWizard';
import { type TabItem, TabList, tabId } from './TabList';

// The donate wizard's four-step rail (arc 101 slice 11, plan doc 05). Presenter:
// the wizard derives every step's state, this paints it.
//
// A real tablist, because that is what it is — four panels, one visible — and a
// screen reader then announces "step 2 of 4, selected" without any of it being
// re-stated in copy. A LOCKED step is `aria-disabled` rather than removed: the
// donor should see that a server and a key are coming, and why they cannot reach
// them yet, instead of watching two steps appear out of nowhere after checkout.
//
// The rail only: the wizard renders the panels itself, interleaved with its own
// project gate, as `donate-panel-<step>` — the id `tabPanelId` derives for this
// rail's prefix, so the tab's `aria-controls` lands on it.

const DONATE_TABS = 'donate';

/** Element id of a step's tab — the panel points back at it with `aria-labelledby`. */
export const stepTabId = (step: DonateStep): string => tabId(DONATE_TABS, step);

export type DonateStepRailProps = {
  readonly steps: readonly DonateStep[];
  readonly current: DonateStep;
  readonly stateOf: (step: DonateStep) => StepState;
  readonly onPick: (step: DonateStep) => void;
};

export function DonateStepRail(props: DonateStepRailProps): JSX.Element {
  // Rows are built once and read state through getters, so a step unlocking
  // re-paints its badge in place instead of remounting the rail under the
  // focused tab.
  const items = props.steps.map(
    (step): TabItem => ({
      id: step,
      label: t(`donations.step_${step}`),
      get disabled() {
        return props.stateOf(step) === 'locked';
      },
      trail: () => (
        <span class="badge badge--outline">{t(`donations.state_${props.stateOf(step)}`)}</span>
      ),
    }),
  );

  return (
    <TabList
      label={t('donations.wizard_steps_label')}
      idPrefix={DONATE_TABS}
      items={items}
      selected={props.current}
      onSelect={(id) => {
        const step = props.steps.find((s) => s === id);
        if (step !== undefined) props.onPick(step);
      }}
    />
  );
}
